import React, { useState, useMemo } from 'react';
import { GridSortModel } from '@mui/x-data-grid';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { RestoreDataGrid } from '@/RestoreComponent/DataGrid';
import {
  getOrderBy,
  mergeCommonGridColConfig,
  PAGE_SIZES
} from '@/RestoreComponent/DataGrid/utils';
import {
  dateSlashFmt,
  timeFmt,
  getAppointmentLatestStatus
} from '@/common/utils/appointments';
import { callRestGetEndpoint } from '@/common/utils/api';

const msToMins = ms => ms / 1000 / 60;

// fn to formulate query params
const getQueryParams = (customer, sortOptions, pageSize, page) => ({
  customer,
  orderBy: getOrderBy(sortOptions),
  page,
  pageSize
});

const Appointments = props => {
  const { customerAllData } = props;

  const { enqueueSnackbar } = useSnackbar();

  const initialSort: GridSortModel = [{ field: 'start', sort: 'asc' }];
  const [loading, setLoading] = useState(false);
  const [sortOptions, setSortOptions] = useState(initialSort);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  const [appointments, setAppointments] = useState([]);

  const columnConfig = useMemo(
    () => [
      mergeCommonGridColConfig({
        field: 'start',
        headerName: 'Date & Time',
        type: 'string',
        flex: 2,
        renderCell: p => (
          <span>
            {moment(p?.row?.start).utc().format(dateSlashFmt)}
            &nbsp;&nbsp;&nbsp;&nbsp;
            {moment(p?.row?.start).utc().format(timeFmt)}
          </span>
        )
      }),
      mergeCommonGridColConfig({
        field: 'duration',
        headerName: 'Duration',
        type: 'number',
        flex: 1,
        sortable: false,
        valueGetter: p =>
          msToMins(moment(p?.row?.end).diff(moment(p?.row?.start)))
      }),
      mergeCommonGridColConfig({
        field: 'purchasable_item__description',
        headerName: 'Description',
        type: 'string',
        flex: 2,
        valueGetter: p => p?.row?.purchasable_item__description
      }),
      mergeCommonGridColConfig({
        field: 'status',
        headerName: 'Status',
        type: 'string',
        flex: 1,
        sortable: false,
        valueGetter: p => getAppointmentLatestStatus(p?.row?.status_type)
      }),
      mergeCommonGridColConfig({
        field: 'store',
        headerName: 'Studio',
        type: 'string',
        flex: 1,
        valueGetter: p => p?.row?.store_code
      }),
      mergeCommonGridColConfig({
        field: 'notes',
        headerName: 'Notes',
        type: 'string',
        flex: 3,
        renderCell: p => (
          <span title={p?.row?.notes} className="ellipsify">
            {p?.row?.notes}
          </span>
        )
      })
    ],
    []
  );

  const getAppointment = async () => {
    setLoading(true);
    try {
      const res = await callRestGetEndpoint(
        getQueryParams(customerAllData.id, sortOptions, pageSize, page),
        '/pos_customer/customer_appointments/'
      );
      setAppointments(res?.data);
      setTotalCount(res?.totalCount);
    } catch (e) {
      enqueueSnackbar('A problem was encountered fetching products.', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getAppointment();
  }, [sortOptions, pageSize, page]);

  const sortChange = o => {
    setPage(0);
    if (o.length === 0) {
      const currentOptions = sortOptions[0];
      const nextOptions: GridSortModel = [
        {
          ...currentOptions,
          sort: sortOptions[0].sort === 'asc' ? 'desc' : 'asc'
        }
      ];
      setSortOptions(nextOptions);
    } else {
      setSortOptions(o);
    }
  };

  return (
    <RestoreDataGrid
      rowCount={totalCount}
      columnConfig={columnConfig}
      rows={appointments}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChangeSize={setPageSize}
      onPageChange={setPage}
      sortModel={sortOptions}
      onSortModelChange={sortChange}
    />
  );
};

export default Appointments;
